import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { LiveConversationModel, CallModel, CallMessageModel, EventMessage, TranscriptMessageModel } from '../models';
import type { BusinessPromptBuildResult, BusinessPromptUiAction } from '../components/details/AiConfigNew/types';
import { getTranscriptTimestampMs, normalizeTranscriptTimestamp } from '../helpers/transcriptTimestamps';

export type CallTab = 'active' | 'transcript';

export interface CallState {
  //calls: CallModel[];
  liveConversations: LiveConversationModel[];
  selectedCallSid?: string;
  isMonitoring?: boolean;
  transcriptId?: number;
  selectedTab: CallTab;        // <— new
  businessPromptsResult?: BusinessPromptBuildResult | null;
  businessPromptsUiActions?: {
    callSid: string;
    eventName: string;
    actions: BusinessPromptUiAction[];
    receivedAt: string;
  } | null;
}

const initialState: CallState = {
  //calls: [],
  liveConversations: [],
  selectedCallSid: undefined,
  isMonitoring: false,
  transcriptId: undefined,
  selectedTab: 'active',
  businessPromptsResult: null,
  businessPromptsUiActions: null
};

const promptBuilderBuildEventNames = new Set([
  "BusinessAssistantService_BuildBusinessPromptsAsync",
  "BuildBusinessPromptsRequest",
]);

const promptBuilderUiRefreshEventNames = new Set([
  "BusinessAssistantService_BuildBusinessPromptsAsync",
  "BuildBusinessPromptsRequest",
  "BusinessAssistantService_SaveBusinessPromptsAsync",
  "SaveBusinessPromptsRequest",
]);

const isBusinessAssistantCatalogUiRefreshEvent = (eventName?: string) => {
  if (!eventName) return false;
  return (
    eventName.startsWith("BusinessAssistantService_BusinessAssistantCatalog_") ||
    eventName.startsWith("BusinessAssistantCatalog_") ||
    eventName.startsWith("BusinessAssistantService_ManageOfferings_") ||
    eventName.startsWith("BusinessAssistantService_ManageResources_") ||
    eventName.startsWith("BusinessAssistantService_ManageAvailability_") ||
    eventName.startsWith("BusinessAssistantService_ManageCallHandling_") ||
    eventName.startsWith("BusinessAssistantCallHandlingService_ManageCallHandling_") ||
    eventName.startsWith("BusinessAssistantService_ManageNotifications_")
  );
};

const normalizePromptUiActions = (value: any): BusinessPromptUiAction[] => {
  const raw = value?.uiActions ?? value?.UiActions;
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item: any) => ({
      action: item?.action ?? item?.Action ?? "",
      businessId: item?.businessId ?? item?.BusinessId,
      keyName: item?.keyName ?? item?.KeyName,
    }))
    .filter((item: BusinessPromptUiAction) => !!item.action);
};

export const normalizePromptBuildResult = (value: any): BusinessPromptBuildResult => {
  const parsedPackageRaw = value?.parsedPackage ?? value?.ParsedPackage;
  const metaRaw = parsedPackageRaw?.meta ?? parsedPackageRaw?.Meta;
  const blocksRaw = parsedPackageRaw?.blocks ?? parsedPackageRaw?.Blocks ?? [];

  const parsedPackage = parsedPackageRaw
    ? {
        meta: metaRaw
          ? {
              version: metaRaw.version ?? metaRaw.Version ?? 0,
              mode: metaRaw.mode ?? metaRaw.Mode ?? "",
              businessDomain: metaRaw.businessDomain ?? metaRaw.BusinessDomain ?? "",
              intents: metaRaw.intents ?? metaRaw.Intents ?? [],
              blockIdsInOrder: metaRaw.blockIdsInOrder ?? metaRaw.BlockIdsInOrder ?? [],
            }
          : {
              version: 0,
              mode: "",
              businessDomain: "",
              intents: [],
              blockIdsInOrder: [],
            },
        changeLog: parsedPackageRaw.changeLog ?? parsedPackageRaw.ChangeLog ?? [],
        missingInfo: parsedPackageRaw.missingInfo ?? parsedPackageRaw.MissingInfo ?? [],
        blocks: Array.isArray(blocksRaw)
          ? blocksRaw.map((block: any) => ({
              blockId: block.blockId ?? block.BlockId ?? "",
              rawBlockType: block.rawBlockType ?? block.RawBlockType ?? "",
              blockType: block.blockType ?? block.BlockType ?? "",
              promptTypeKey: block.promptTypeKey ?? block.PromptTypeKey ?? block.BlockId ?? "",
              promptTypeDisplayName:
                block.promptTypeDisplayName ?? block.PromptTypeDisplayName ?? "",
              promptTypeLabel: block.promptTypeLabel ?? block.PromptTypeLabel ?? "",
              promptName: block.promptName ?? block.PromptName ?? "",
              promptTypeId: block.promptTypeId ?? block.PromptTypeId,
              promptId: block.promptId ?? block.PromptId,
              uiSummaryRaw: block.uiSummaryRaw ?? block.UiSummaryRaw ?? "",
              uiSummaryLines: block.uiSummaryLines ?? block.UiSummaryLines ?? [],
              promptText: block.promptText ?? block.PromptText ?? "",
              order: block.order ?? block.Order ?? 0,
              intentSlug: block.intentSlug ?? block.IntentSlug ?? null,
            }))
          : [],
      }
    : undefined;

  const promptRowsRaw = value?.promptRows ?? value?.PromptRows ?? [];
  const promptRows = Array.isArray(promptRowsRaw)
    ? promptRowsRaw.map((row: any) => ({
        promptId: row.promptId ?? row.PromptId,
        businessId: row.businessId ?? row.BusinessId ?? 0,
        assistantTypeId: row.assistantTypeId ?? row.AssistantTypeId,
        assistantTypeName: row.assistantTypeName ?? row.AssistantTypeName,
        promptTypeId: row.promptTypeId ?? row.PromptTypeId,
        typeOfPrompt: row.typeOfPrompt ?? row.TypeOfPrompt ?? row.promptTypeLabel ?? row.PromptTypeLabel,
        name: row.name ?? row.Name ?? row.promptName ?? row.PromptName,
        promptOrder: row.promptOrder ?? row.PromptOrder ?? 0,
        aiPrompt: row.aiPrompt ?? row.AiPrompt ?? row.promptText ?? row.PromptText ?? "",
        latestVersionNo: row.latestVersionNo ?? row.LatestVersionNo,
        latestVersionCreated: row.latestVersionCreated ?? row.LatestVersionCreated,
        uiSummary: row.uiSummary ?? row.UiSummary ?? "",
        uiSummaryLines: row.uiSummaryLines ?? row.UiSummaryLines ?? [],
        promptTypeKey: row.promptTypeKey ?? row.PromptTypeKey ?? "",
        promptTypeDisplayName: row.promptTypeDisplayName ?? row.PromptTypeDisplayName ?? "",
      }))
    : [];

  return {
    businessId: value?.businessId ?? value?.BusinessId ?? 0,
    parseSucceeded: value?.parseSucceeded ?? value?.ParseSucceeded ?? false,
    parseError: value?.parseError ?? value?.ParseError,
    rawPromptPackageText:
      value?.rawPromptPackageText ?? value?.RawPromptPackageText ?? "",
    parsedPackage,
    promptRows,
    uiActions: normalizePromptUiActions(value),
    source: value?.source ?? value?.Source,
  };
};

const getOrCreateLiveConversation = (state: CallState, callSid: string): LiveConversationModel => {
  let conversation = state.liveConversations.find((item: LiveConversationModel) => item.callSid === callSid);
  if (conversation) {
    if (!conversation.pendingFunctionCalls) {
      conversation.pendingFunctionCalls = [];
    }
    return conversation;
  }

  conversation = {
    callSid,
    callStartedAt: new Date().toISOString(),
    callEndedAt: "",
    hasEnded: false,
    messages: [],
    pendingFunctionCalls: []
  };

  state.liveConversations.push(conversation);
  return conversation;
};

type LiveMessagePayload = {
  callSid: string;
  text: string;
  role: string;
  streamKey?: string;
  isStreaming?: boolean;
  timestamp?: string;
};

type FinishLiveMessageStreamPayload = {
  callSid: string;
  role: string;
  streamKey?: string;
};

type MergeLiveConversationTranscriptPayload = {
  callSid: string;
  transcript: TranscriptMessageModel[];
};

const isAssistantResponseStreamKey = (streamKey?: string) =>
  typeof streamKey === 'string' && streamKey.startsWith('assistant-response:');

const getSortableTimestamp = (timestamp?: string) => {
  return getTranscriptTimestampMs(timestamp);
};

const toIsoTimestamp = (date: Date) => date.toISOString();

const getCanonicalTimestamp = (timestamp?: string) =>
  normalizeTranscriptTimestamp(timestamp) || toIsoTimestamp(new Date());

const getTimestampBefore = (timestamp?: string) => {
  const time = getSortableTimestamp(timestamp);
  return Number.isFinite(time) && time !== Number.MAX_SAFE_INTEGER
    ? toIsoTimestamp(new Date(time - 1))
    : toIsoTimestamp(new Date());
};

const sortLiveMessagesByTimestamp = (conversation: LiveConversationModel) => {
  conversation.messages = conversation.messages
    .map((message, index) => ({ message, index }))
    .sort((left, right) =>
      getSortableTimestamp(left.message.timestamp) - getSortableTimestamp(right.message.timestamp) ||
      left.index - right.index
    )
    .map((item) => item.message);
};

const findInsertionIndexBeforeActiveAssistantTail = (
  conversation: LiveConversationModel
) => {
  let insertionIndex = conversation.messages.length;
  let foundAssistantResponseTail = false;

  for (let index = conversation.messages.length - 1; index >= 0; index -= 1) {
    const message = conversation.messages[index];

    if (isAssistantResponseStreamKey(message.streamKey) && message.role === 'assistant') {
      foundAssistantResponseTail = true;
      insertionIndex = index;
      continue;
    }

    if (message.role === 'event' && foundAssistantResponseTail) {
      insertionIndex = index;
      continue;
    }

    break;
  }

  return foundAssistantResponseTail ? insertionIndex : conversation.messages.length;
};

const findMessageIndexByStreamKey = (
  conversation: LiveConversationModel,
  role: string,
  streamKey?: string
) => {
  if (!streamKey) return -1;

  for (let index = conversation.messages.length - 1; index >= 0; index -= 1) {
    const message = conversation.messages[index];
    if (message.role === role && message.streamKey === streamKey) {
      return index;
    }
  }

  return -1;
};

const finalizeStreamState = (message: { streamKey?: string }, payload: LiveMessagePayload) => {
  if (payload.streamKey === undefined) return;

  if (payload.isStreaming) {
    message.streamKey = payload.streamKey;
    return;
  }

  delete message.streamKey;
};

const applyPayloadTimestamp = (
  message: { timestamp: string },
  payload: Pick<LiveMessagePayload, 'timestamp'>
) => {
  if (payload.timestamp) {
    message.timestamp = getCanonicalTimestamp(payload.timestamp);
  }
};

const shouldPreserveLongerAssistantStreamText = (
  currentText: string,
  payload: LiveMessagePayload
) =>
  payload.role === 'assistant' &&
  payload.isStreaming === false &&
  currentText.trim().length > payload.text.trim().length;

const canReuseLastMessage = (
  lastMessage: { role: string; streamKey?: string } | undefined,
  payload: LiveMessagePayload
) => {
  if (!lastMessage || lastMessage.role !== payload.role) {
    return false;
  }

  if (payload.streamKey !== undefined) {
    return lastMessage.streamKey === payload.streamKey;
  }

  return true;
};

const pushLiveMessage = (
  conversation: LiveConversationModel,
  text: string,
  role: string,
  streamKey?: string,
  isStreaming?: boolean,
  timestamp?: string
) => {
  const insertionIndex = role === 'user'
    ? findInsertionIndexBeforeActiveAssistantTail(conversation)
    : conversation.messages.length;
  const message = {
    text,
    timestamp: timestamp ? getCanonicalTimestamp(timestamp) : (
      role === 'user' && insertionIndex < conversation.messages.length
        ? getTimestampBefore(conversation.messages[insertionIndex].timestamp)
        : new Date().toISOString()
    ),
    role,
    streamKey: isStreaming ? streamKey : undefined
  };

  if (role === 'user' && insertionIndex < conversation.messages.length) {
    conversation.messages.splice(insertionIndex, 0, message);
    sortLiveMessagesByTimestamp(conversation);
    return;
  }

  conversation.messages.push(message);
  sortLiveMessagesByTimestamp(conversation);
};

const normalizeTranscriptText = (value: string | undefined | null) =>
  (value ?? '').replace(/\s+/g, ' ').trim();

const buildPersistedTranscriptKey = (message: TranscriptMessageModel) => {
  if (message.conversationId > 0) {
    return `conversation:${message.conversationId}`;
  }

  return [
    'transcript',
    (message.role ?? '').toLowerCase(),
    message.talkedOn ?? '',
    normalizeTranscriptText(message.speech).toLowerCase(),
  ].join(':');
};

const findUnclaimedMatchingMessageIndex = (
  conversation: LiveConversationModel,
  role: string,
  speech: string,
  claimedIndexes: Set<number>
) => {
  const normalizedSpeech = normalizeTranscriptText(speech).toLowerCase();
  if (!normalizedSpeech) return -1;

  return conversation.messages.findIndex((message, index) =>
    !claimedIndexes.has(index) &&
    !message.persistedTranscriptKey &&
    message.role === role &&
    normalizeTranscriptText(message.text).toLowerCase() === normalizedSpeech
  );
};


export const callSlice = createSlice({
  name: 'calls',
  initialState,
  reducers: {
    setBusinessPromptsResult: (state, action: PayloadAction<BusinessPromptBuildResult | null | undefined>) => {
      if (!action.payload) {
        state.businessPromptsResult = null;
        return;
      }
      state.businessPromptsResult = normalizePromptBuildResult(action.payload);
    },
    clearBusinessPromptsUiActions: (state) => {
      state.businessPromptsUiActions = null;
    },
    setTranscriptId: (state, action: PayloadAction<number>) => {
      state.transcriptId = action.payload;
      state.selectedTab = 'transcript';
    },
    setSelectedTab: (state, action: PayloadAction<CallTab>) => {
      // prevent selecting transcript if none loaded
      if (action.payload === 'transcript' && !state.transcriptId) return;
      state.selectedTab = action.payload;
    },
    clearTranscript: (state) => {
      state.transcriptId = undefined;
      state.selectedTab = 'active';
    },
    setSelectedCallSid: (state, action: PayloadAction<{ callSid: string | undefined, isMonitoring?: boolean }>) => {
      state.selectedCallSid = action.payload.callSid;
      state.isMonitoring = action.payload.isMonitoring ? action.payload.isMonitoring : false;
      // Code below will always go back to Business Assistant - however for now i don't want it to connect back to that
      // if (action.payload) {
      //   state.selectedCallSid = action.payload;
      // } else {
      //   const businessAssistantCallSid = state.liveConversations.find(conversation => conversation.callSid.startsWith("BA"))?.callSid;
      //   state.selectedCallSid = businessAssistantCallSid;
      // }
    },
    addLiveConversation: (state, action: PayloadAction<string>) => {
      if (state.liveConversations.find((conversation: LiveConversationModel) => conversation.callSid === action.payload)) return;

      state.liveConversations.push({
        callSid: action.payload,
        messages: [],
        callStartedAt: new Date().toISOString(),
        callEndedAt: "",
        hasEnded: false,
        pendingFunctionCalls: []
      });
    },
    mergeLiveConversationTranscript: (state, action: PayloadAction<MergeLiveConversationTranscriptPayload>) => {
      if (!action.payload.callSid || !Array.isArray(action.payload.transcript)) return;

      const conversation = getOrCreateLiveConversation(state, action.payload.callSid);
      const claimedLiveMessageIndexes = new Set<number>();
      const persistedMessages = [...action.payload.transcript]
        .filter((message) => {
          const role = (message.role ?? '').toLowerCase();
          return (role === 'user' || role === 'assistant' || role === 'event') &&
            normalizeTranscriptText(message.speech).length > 0;
        })
        .sort((left, right) =>
          getSortableTimestamp(left.talkedOn) - getSortableTimestamp(right.talkedOn)
        );

      persistedMessages.forEach((message) => {
        const role = (message.role ?? '').toLowerCase();
        const transcriptKey = buildPersistedTranscriptKey(message);
        const existingPersistedIndex = conversation.messages.findIndex(
          (item) => item.persistedTranscriptKey === transcriptKey
        );

        if (existingPersistedIndex >= 0) {
          conversation.messages[existingPersistedIndex].text = message.speech;
          conversation.messages[existingPersistedIndex].role = role;
          conversation.messages[existingPersistedIndex].timestamp = getCanonicalTimestamp(message.talkedOn);
          return;
        }

        const matchingLiveMessageIndex = findUnclaimedMatchingMessageIndex(
          conversation,
          role,
          message.speech,
          claimedLiveMessageIndexes
        );

        if (matchingLiveMessageIndex >= 0) {
          conversation.messages[matchingLiveMessageIndex].persistedTranscriptKey = transcriptKey;
          conversation.messages[matchingLiveMessageIndex].timestamp = getCanonicalTimestamp(message.talkedOn);
          claimedLiveMessageIndexes.add(matchingLiveMessageIndex);
          return;
        }

        conversation.messages.push({
          text: message.speech,
          timestamp: getCanonicalTimestamp(message.talkedOn),
          role,
          persistedTranscriptKey: transcriptKey
        });
      });

      sortLiveMessagesByTimestamp(conversation);
    },
    setLiveConversationSimulation: (
      state,
      action: PayloadAction<{
        callSid: string;
        simulationMode: 'aiCaller';
        simulationInstructions: string;
        simulationCallerName: string;
        simulationCallerPhoneNumber: string;
        simulationCallerEmailAddress: string;
        simulationCallerAddressLine1: string;
        simulationCallerPostCode: string;
        simulationCallerType: string;
        simulationCallerVoice: string;
      }>
    ) => {
      const conversation = state.liveConversations.find((item: LiveConversationModel) => item.callSid === action.payload.callSid);
      if (conversation) {
        conversation.simulationMode = action.payload.simulationMode;
        conversation.simulationInstructions = action.payload.simulationInstructions;
        conversation.simulationCallerName = action.payload.simulationCallerName;
        conversation.simulationCallerPhoneNumber = action.payload.simulationCallerPhoneNumber;
        conversation.simulationCallerEmailAddress = action.payload.simulationCallerEmailAddress;
        conversation.simulationCallerAddressLine1 = action.payload.simulationCallerAddressLine1;
        conversation.simulationCallerPostCode = action.payload.simulationCallerPostCode;
        conversation.simulationCallerType = action.payload.simulationCallerType;
        conversation.simulationCallerVoice = action.payload.simulationCallerVoice;
        return;
      }

      state.liveConversations.push({
        callSid: action.payload.callSid,
        messages: [],
        callStartedAt: new Date().toISOString(),
        callEndedAt: "",
        hasEnded: false,
        pendingFunctionCalls: [],
        simulationMode: action.payload.simulationMode,
        simulationInstructions: action.payload.simulationInstructions,
        simulationCallerName: action.payload.simulationCallerName,
        simulationCallerPhoneNumber: action.payload.simulationCallerPhoneNumber,
        simulationCallerEmailAddress: action.payload.simulationCallerEmailAddress,
        simulationCallerAddressLine1: action.payload.simulationCallerAddressLine1,
        simulationCallerPostCode: action.payload.simulationCallerPostCode,
        simulationCallerType: action.payload.simulationCallerType,
        simulationCallerVoice: action.payload.simulationCallerVoice
      });
    },
    startPendingFunctionCall: (
      state,
      action: PayloadAction<{ callSid: string; callId: string; name: string; startedAt?: string }>
    ) => {
      const conversation = getOrCreateLiveConversation(state, action.payload.callSid);
      const pendingFunctionCalls = conversation.pendingFunctionCalls ?? (conversation.pendingFunctionCalls = []);
      if (conversation.hasEnded || conversation.messages.some(message => message.functionCallId === action.payload.callId)) return;
      const existingFunctionCall = pendingFunctionCalls.find((item) => item.callId === action.payload.callId);

      if (existingFunctionCall) {
        existingFunctionCall.name = action.payload.name;
        if (action.payload.startedAt) {
          existingFunctionCall.startedAt = getCanonicalTimestamp(action.payload.startedAt);
        }
        return;
      }

      pendingFunctionCalls.push({
        callId: action.payload.callId,
        name: action.payload.name,
        startedAt: action.payload.startedAt ? getCanonicalTimestamp(action.payload.startedAt) : new Date().toISOString()
      });
    },
    finishPendingFunctionCall: (
      state,
      action: PayloadAction<{ callSid: string; callId?: string; name?: string }>
    ) => {
      const conversation = state.liveConversations.find((item: LiveConversationModel) => item.callSid === action.payload.callSid);
      if (!conversation?.pendingFunctionCalls?.length) return;

      if (action.payload.callId) {
        conversation.pendingFunctionCalls = conversation.pendingFunctionCalls.filter(
          (item) => item.callId !== action.payload.callId
        );
        return;
      }

      if (action.payload.name) {
        const itemIndex = conversation.pendingFunctionCalls.findIndex((item) => item.name === action.payload.name);
        if (itemIndex >= 0) {
          conversation.pendingFunctionCalls.splice(itemIndex, 1);
        }
      }
    },

    setLiveConversationCanEnd: (state, action: PayloadAction<{ callSid: string; canEndCall: boolean }>) => {
      const conversation = state.liveConversations.find((c: LiveConversationModel) => c.callSid === action.payload.callSid);
      if (!conversation) return;
      conversation.canEndCall = action.payload.canEndCall;
    },

    setLiveConversationIsTwilioClientCall: (state, action: PayloadAction<{ callSid: string; isTwilioClientCall: boolean }>) => {
      const conversation = state.liveConversations.find((c: LiveConversationModel) => c.callSid === action.payload.callSid);
      if (!conversation) return;
      conversation.isTwilioClientCall = action.payload.isTwilioClientCall;
    },

    addLiveEvent: (state, action: PayloadAction<{ callSid: string, callId?: string, name?: string, itemData?: unknown, timestamp?: string }>) => {
      const existing = state.liveConversations.find(item => item.callSid === action.payload.callSid);
      if (action.payload.callId && existing?.messages.some(message => message.functionCallId === action.payload.callId)) return;
      let parsedItemData: any = null;
      let rawItemData: any = action.payload.itemData ?? "";

      if (rawItemData) {
        if (typeof rawItemData === "string") {
          try {
            parsedItemData = JSON.parse(rawItemData);
          } catch (error) {
            parsedItemData = null;
          }
        } else if (typeof rawItemData === "object") {
          parsedItemData = rawItemData;
          try {
            rawItemData = JSON.stringify(rawItemData);
          } catch (error) {
            rawItemData = "";
          }
        }
      }

      const event = action.payload.name ? action.payload.name : parsedItemData?.name ?? parsedItemData?.functionName;
      let customEventName = event ?? "Function result";

      const responsePayload = parsedItemData?.response ?? parsedItemData?.output ?? parsedItemData;
      if (event && (promptBuilderUiRefreshEventNames.has(event) || isBusinessAssistantCatalogUiRefreshEvent(event))) {
        const uiActions = normalizePromptUiActions(responsePayload);
        if (uiActions.length > 0) {
          state.businessPromptsUiActions = {
            callSid: action.payload.callSid,
            eventName: event,
            actions: uiActions,
            receivedAt: new Date().toISOString(),
          };
        }
      }

      if (event && promptBuilderBuildEventNames.has(event)) {
        let result: BusinessPromptBuildResult | null = null;
        let rawPayloadText = "";

        if (responsePayload && typeof responsePayload === "object") {
          result = responsePayload as BusinessPromptBuildResult;
        } else if (typeof responsePayload === "string") {
          rawPayloadText = responsePayload;
        } else if (rawItemData) {
          rawPayloadText = rawItemData;
        }

        if (!result && rawPayloadText) {
          try {
            result = JSON.parse(rawPayloadText) as BusinessPromptBuildResult;
          } catch (error) {
            result = {
              businessId: 0,
              parseSucceeded: false,
              parseError: "Failed to parse prompt builder response.",
              rawPromptPackageText: rawPayloadText
            };
          }
        }

        if (result) {
          const normalized = normalizePromptBuildResult(result);
          const hasBuildPayload =
            (normalized.promptRows?.length ?? 0) > 0 ||
            (normalized.parsedPackage?.blocks?.length ?? 0) > 0 ||
            !!normalized.rawPromptPackageText ||
            !!normalized.parseError;

          if (hasBuildPayload) {
            state.businessPromptsResult = {
              ...normalized,
              source: "callSlice",
            };
          }
        }
      }

      if (event === "FnolService_GetPolicy") {
          customEventName = "Policy Retrieved";
      } else if (event === "FnolService_SaveIncidentAsync") {
          customEventName = "Incident Created";
      } else if (event === "PhoneCallService_HangUpCallAndSendClaimEmailAsync") {
          customEventName = "Claim Email Sent";
      }

      const conversation = state.liveConversations.find((conversation: LiveConversationModel) => conversation.callSid === action.payload.callSid);

      if (conversation) {
        conversation.messages.push({
          text: customEventName,
          functionCallId: action.payload.callId,
          timestamp: action.payload.timestamp ? getCanonicalTimestamp(action.payload.timestamp) : new Date().toISOString(),
          role: "event",
          response: responsePayload
        });
        sortLiveMessagesByTimestamp(conversation);
      } else {
        state.liveConversations.push({
          callSid: action.payload.callSid,
          callStartedAt: new Date().toISOString(),
          callEndedAt: "",
          hasEnded: false,
          messages: [{
            text: customEventName,
            functionCallId: action.payload.callId,
            timestamp: action.payload.timestamp ? getCanonicalTimestamp(action.payload.timestamp) : new Date().toISOString(),
            role: "event",
            response: responsePayload
          }]
        });
      }
    },
    addUserMessage: (state, action: PayloadAction<{ callSid: string, text: string, timestamp?: string }>) => {
      const conversation = state.liveConversations.find((conversation: LiveConversationModel) => conversation.callSid === action.payload.callSid);

      if (conversation) {
        const insertionIndex = findInsertionIndexBeforeActiveAssistantTail(conversation);
        const message = {
          text: action.payload.text,
          timestamp: action.payload.timestamp ? getCanonicalTimestamp(action.payload.timestamp) : (
            insertionIndex < conversation.messages.length
              ? getTimestampBefore(conversation.messages[insertionIndex].timestamp)
              : new Date().toISOString()
          ),
          role: "user"
        };
        if (insertionIndex < conversation.messages.length) {
          conversation.messages.splice(insertionIndex, 0, message);
        } else {
          conversation.messages.push(message);
        }
        sortLiveMessagesByTimestamp(conversation);
      } else {
        state.liveConversations.push({
          callSid: action.payload.callSid,
          callStartedAt: new Date().toISOString(),
          callEndedAt: "",
          hasEnded: false,
          messages: [{
            text: action.payload.text,
            timestamp: action.payload.timestamp ? getCanonicalTimestamp(action.payload.timestamp) : new Date().toISOString(),
            role: "user"
          }]
        });
      }
    },

    addLiveMessage: (state, action: PayloadAction<LiveMessagePayload>) => {
      const text = action.payload.text;
      const role = action.payload.role;
      const conversation = getOrCreateLiveConversation(state, action.payload.callSid);
      pushLiveMessage(conversation, text, role, action.payload.streamKey, action.payload.isStreaming, action.payload.timestamp);
    },
    upsertLiveMessage: (state, action: PayloadAction<LiveMessagePayload>) => {
      const conversation = getOrCreateLiveConversation(state, action.payload.callSid);
      const streamMessageIndex = findMessageIndexByStreamKey(
        conversation,
        action.payload.role,
        action.payload.streamKey
      );
      const lastMessage = conversation.messages[conversation.messages.length - 1];

      if (streamMessageIndex >= 0) {
        const streamMessage = conversation.messages[streamMessageIndex];
        if (shouldPreserveLongerAssistantStreamText(streamMessage.text, action.payload)) {
          applyPayloadTimestamp(streamMessage, action.payload);
          finalizeStreamState(streamMessage, action.payload);
          sortLiveMessagesByTimestamp(conversation);
          return;
        }

        streamMessage.text = action.payload.text;
        applyPayloadTimestamp(streamMessage, action.payload);
        finalizeStreamState(streamMessage, action.payload);
        sortLiveMessagesByTimestamp(conversation);
        return;
      }

      if (canReuseLastMessage(lastMessage, action.payload)) {
        if (shouldPreserveLongerAssistantStreamText(lastMessage.text, action.payload)) {
          applyPayloadTimestamp(lastMessage, action.payload);
          finalizeStreamState(lastMessage, action.payload);
          sortLiveMessagesByTimestamp(conversation);
          return;
        }

        lastMessage.text = action.payload.text;
        applyPayloadTimestamp(lastMessage, action.payload);
        finalizeStreamState(lastMessage, action.payload);
        sortLiveMessagesByTimestamp(conversation);
        return;
      }

      pushLiveMessage(
        conversation,
        action.payload.text,
        action.payload.role,
        action.payload.streamKey,
        action.payload.isStreaming,
        action.payload.timestamp
      );
    },
    setCallEnded: (state, action: PayloadAction<string>) => {
      const conversation = state.liveConversations.find((conversation: LiveConversationModel) => conversation.callSid === action.payload);

      if (conversation) {
        conversation.callEndedAt = new Date().toISOString();
        conversation.hasEnded = true;
        conversation.pendingFunctionCalls = [];
      }
    },

    addLiveMessageDelta: (state, action: PayloadAction<LiveMessagePayload>) => {
      const conversation = getOrCreateLiveConversation(state, action.payload.callSid);
      const streamMessageIndex = findMessageIndexByStreamKey(
        conversation,
        action.payload.role,
        action.payload.streamKey
      );
      const lastMessage = conversation.messages[conversation.messages.length - 1];

      if (streamMessageIndex >= 0) {
        const streamMessage = conversation.messages[streamMessageIndex];
        streamMessage.text += action.payload.text;
        applyPayloadTimestamp(streamMessage, action.payload);
        finalizeStreamState(streamMessage, action.payload);
        sortLiveMessagesByTimestamp(conversation);
        return;
      }

      if (canReuseLastMessage(lastMessage, action.payload)) {
        lastMessage.text += action.payload.text;
        applyPayloadTimestamp(lastMessage, action.payload);
        finalizeStreamState(lastMessage, action.payload);
        sortLiveMessagesByTimestamp(conversation);
        return;
      }

      pushLiveMessage(
        conversation,
        action.payload.text,
        action.payload.role,
        action.payload.streamKey,
        action.payload.isStreaming,
        action.payload.timestamp
      );
    },
    finishLiveMessageStream: (state, action: PayloadAction<FinishLiveMessageStreamPayload>) => {
      const conversation = state.liveConversations.find((item: LiveConversationModel) => item.callSid === action.payload.callSid);
      if (!conversation) return;

      const streamMessageIndex = findMessageIndexByStreamKey(
        conversation,
        action.payload.role,
        action.payload.streamKey
      );

      if (streamMessageIndex >= 0) {
        delete conversation.messages[streamMessageIndex].streamKey;
      }
    },
    setLogoutCalls: (state) => {
      state = initialState;
      return state;
    },
  },
});

export const { setBusinessPromptsResult, clearBusinessPromptsUiActions, setTranscriptId, setSelectedTab, clearTranscript, setSelectedCallSid, addLiveConversation, mergeLiveConversationTranscript, setLiveConversationSimulation, setLiveConversationCanEnd, setLiveConversationIsTwilioClientCall, startPendingFunctionCall, finishPendingFunctionCall, addLiveEvent, addUserMessage, addLiveMessage, upsertLiveMessage, addLiveMessageDelta, finishLiveMessageStream, setCallEnded, setLogoutCalls } = callSlice.actions;

export default callSlice.reducer;

export const getSelectedCall = (state: RootState) => {
  return state.calls.liveConversations.find((conversation: LiveConversationModel) => conversation.callSid === state.calls.selectedCallSid);
};
